<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
// SiteUp (2026-08-08): configuração da regra GLOBAL de auto-criação de card por IA.
// Antes cada funil tinha sua própria aba "IA" e o gatilho pegava o primeiro funil
// habilitado, ignorando os demais — para rotear entre funis o cliente precisava
// criar um funil de triagem cujas etapas imitavam os outros. Agora a conta decide
// quais funis participam e a IA escolhe funil + etapa numa chamada só.
//
// UI em WIZARD (2026-08-08): a tela empilhava toggle + multi-seleção + fallback +
// prompt de roteamento + 4 checkboxes + prompt de extração + ofertas numa coluna
// só. Virou 4 etapas, uma decisão por tela, no mesmo padrão do wizard de campanha
// de grupos (WhatsappCampaigns.vue / WhatsappGroupCreate.vue): indicador de passo,
// footer Anterior/Próximo, salvar só na última etapa. O modal NÃO fecha no
// backdrop — só X, Cancelar ou Esc (regressão conhecida do padrão de wizard).
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import KanbanAPI from 'dashboard/api/kanban';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  show: { type: Boolean, default: false },
  funnels: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'saved']);

const EXTRACT_FIELDS = [
  {
    key: 'title',
    label: 'Título do card',
    hint: 'Gera título descritivo baseado na conversa',
  },
  {
    key: 'description',
    label: 'Descrição / Resumo',
    hint: 'Resume os pontos principais da conversa',
  },
  {
    key: 'priority',
    label: 'Prioridade',
    hint: 'Detecta urgência (alta/média/baixa)',
  },
  {
    key: 'value',
    label: 'Valor estimado',
    hint: 'Extrai valor monetário mencionado na conversa',
  },
];

// Uma decisão por etapa. A ordem é deliberada: primeiro a decisão estrutural
// (quais funis), depois como escolher entre eles, depois o que preencher no card
// e por último o que é opcional + a conferência antes de gravar.
const STEPS = [
  {
    key: 'funnels',
    label: 'Funis',
    title: 'Quais funis a IA pode usar',
    subtitle:
      'A IA escolhe o funil e a etapa entre os que você marcar aqui. O fallback recebe o card quando ela não conseguir decidir.',
  },
  {
    key: 'routing',
    label: 'Roteamento',
    title: 'Como escolher o funil e a etapa',
    subtitle:
      'Prompt único: a IA vê as etapas de todos os funis marcados de uma vez e decide numa chamada só.',
  },
  {
    key: 'fields',
    label: 'Preenchimento',
    title: 'O que a IA preenche no card',
    subtitle:
      'Marque os campos que a IA deve gerar e escreva as regras de extração.',
  },
  {
    key: 'advanced',
    label: 'Revisão',
    title: 'Opções e revisão',
    subtitle: 'Confira o que será salvo antes de confirmar.',
  },
];

const TOTAL_STEPS = STEPS.length;
const MIN_HOURS = 1;
const MAX_HOURS = 168;
const DEFAULT_HOURS = 6;

const blankConfig = () => ({
  enabled: false,
  funnel_ids: [],
  fallback_funnel_id: null,
  routing_prompt: '',
  custom_prompt: '',
  extract_fields: {
    title: true,
    description: true,
    priority: true,
    value: true,
  },
  attach_offers: false,
  auto_update_enabled: false,
  auto_update_after_hours: DEFAULT_HOURS,
});

const form = ref(blankConfig());
const loading = ref(false);
const saving = ref(false);
const errorMessage = ref('');
const currentStep = ref(1);
const dialogRef = ref(null);
let dialogOpener = null;

const activeFunnels = computed(() => props.funnels.filter(f => f.active));

// Funis que já têm a config antiga por funil — sinalizamos porque, com a regra
// global ligada, essa config deixa de valer para a CRIAÇÃO de card.
const legacyFunnels = computed(() =>
  activeFunnels.value.filter(f => f.settings?.ai_auto_create?.enabled)
);

const selectedCount = computed(() => form.value.funnel_ids.length);

const currentStepKey = computed(() => STEPS[currentStep.value - 1].key);
const currentStepMeta = computed(() => STEPS[currentStep.value - 1]);

const canSave = computed(() => {
  if (!form.value.enabled) return true;
  return selectedCount.value > 0;
});

// Só a etapa 1 tem mínimo obrigatório: ou a regra está desligada, ou pelo menos
// um funil está marcado (mesma regra do canSave, que continua guardando o save).
const canProceed = computed(() => {
  if (loading.value) return false;
  if (currentStepKey.value === 'funnels') return canSave.value;
  return true;
});

const isSelected = funnelId => form.value.funnel_ids.includes(funnelId);

const toggleFunnel = funnelId => {
  const list = form.value.funnel_ids;
  const index = list.indexOf(funnelId);
  if (index === -1) list.push(funnelId);
  else list.splice(index, 1);

  // Fallback tem que continuar existindo: se o funil escolhido como fallback saiu
  // da lista, ele ainda é válido (pode ser um funil de triagem fora da regra),
  // mas se foi removido da conta o backend zera de qualquer forma.
};

const selectedFunnelNames = computed(() =>
  props.funnels.filter(f => isSelected(f.id)).map(f => f.name)
);

const fallbackFunnelName = computed(() => {
  const funnel = props.funnels.find(
    f => f.id === form.value.fallback_funnel_id
  );
  return funnel?.name || 'Primeiro funil marcado';
});

const selectedFieldLabels = computed(() =>
  EXTRACT_FIELDS.filter(field => form.value.extract_fields[field.key]).map(
    field => field.label
  )
);

const normalizeHours = raw => {
  const hours = Math.round(Number(raw));
  if (!Number.isFinite(hours) || hours <= 0) return DEFAULT_HOURS;
  return Math.min(Math.max(hours, MIN_HOURS), MAX_HOURS);
};

// Payload explícito: o contrato gravado é exatamente estas 9 chaves. Montar à mão
// evita reenviar lixo que porventura venha do GET e mantém o shape estável.
const buildPayload = () => ({
  enabled: Boolean(form.value.enabled),
  funnel_ids: [...form.value.funnel_ids],
  fallback_funnel_id: form.value.fallback_funnel_id ?? null,
  routing_prompt: form.value.routing_prompt || '',
  custom_prompt: form.value.custom_prompt || '',
  extract_fields: {
    title: Boolean(form.value.extract_fields.title),
    description: Boolean(form.value.extract_fields.description),
    priority: Boolean(form.value.extract_fields.priority),
    value: Boolean(form.value.extract_fields.value),
  },
  attach_offers: Boolean(form.value.attach_offers),
  auto_update_enabled: Boolean(form.value.auto_update_enabled),
  auto_update_after_hours: normalizeHours(form.value.auto_update_after_hours),
});

const fetchSettings = async () => {
  loading.value = true;
  errorMessage.value = '';
  try {
    const { data } = await KanbanAPI.getGlobalAiSettings();
    const stored = data?.kanban_ai_auto_create || {};
    form.value = { ...blankConfig(), ...stored };
    // Backend devolve extract_fields completo; se vier vazio (conta nova), mantém o default.
    if (!stored.extract_fields || !Object.keys(stored.extract_fields).length) {
      form.value.extract_fields = blankConfig().extract_fields;
    }
    form.value.funnel_ids = Array.isArray(stored.funnel_ids)
      ? [...stored.funnel_ids]
      : [];
  } catch (e) {
    errorMessage.value = 'Não foi possível carregar a configuração.';
  } finally {
    loading.value = false;
  }
};

// --- Ciclo de vida do diálogo -------------------------------------------------
// Backdrop NÃO fecha (regressão conhecida do wizard de grupos: clique que termina
// sobre o overlay depois do re-layout descartava o formulário inteiro). Fecha só
// por X, Cancelar ou Esc, com foco preso no diálogo e devolvido ao abridor.
// `keydownHandler` guarda o listener registrado para poder removê-lo sem que as
// funções precisem se referenciar antes de existir.
let keydownHandler = null;

const teardownDialog = () => {
  if (keydownHandler) document.removeEventListener('keydown', keydownHandler);
  keydownHandler = null;
  const opener = dialogOpener;
  dialogOpener = null;
  if (opener && typeof opener.focus === 'function') opener.focus();
};

const requestClose = () => {
  teardownDialog();
  emit('close');
};

const trapFocus = event => {
  const dialog = dialogRef.value;
  if (!dialog) return;
  const selector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusable = Array.from(dialog.querySelectorAll(selector));
  if (!focusable.length) {
    event.preventDefault();
    dialog.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (active === dialog || !dialog.contains(active)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  } else if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

const handleDialogKeydown = event => {
  if (!props.show) return;
  if (event.key === 'Tab') {
    trapFocus(event);
    return;
  }
  if (event.key === 'Escape') {
    event.preventDefault();
    requestClose();
  }
};

const openDialog = () => {
  dialogOpener = document.activeElement;
  if (keydownHandler) document.removeEventListener('keydown', keydownHandler);
  keydownHandler = handleDialogKeydown;
  document.addEventListener('keydown', keydownHandler);
  currentStep.value = 1;
  fetchSettings();
  nextTick(() => dialogRef.value?.focus());
};

const nextStep = event => {
  event?.preventDefault?.();
  event?.stopPropagation?.();
  if (!canProceed.value || currentStep.value >= TOTAL_STEPS) return;
  errorMessage.value = '';
  currentStep.value += 1;
};

const prevStep = event => {
  event?.preventDefault?.();
  event?.stopPropagation?.();
  if (currentStep.value > 1) currentStep.value -= 1;
};

const save = async () => {
  if (saving.value || !canSave.value) return;
  saving.value = true;
  errorMessage.value = '';
  try {
    const { data } = await KanbanAPI.updateGlobalAiSettings(buildPayload());
    emitter.emit('newToastMessage', {
      message: 'Regra de IA do Kanban salva',
      type: 'success',
    });
    emit('saved', data?.kanban_ai_auto_create || {});
    requestClose();
  } catch (e) {
    errorMessage.value =
      e?.response?.data?.error || 'Não foi possível salvar a configuração.';
  } finally {
    saving.value = false;
  }
};

watch(
  () => props.show,
  value => {
    if (value) openDialog();
    else teardownDialog();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (keydownHandler) document.removeEventListener('keydown', keydownHandler);
});
</script>

<template>
  <div
    v-if="show"
    ref="dialogRef"
    class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="kanban-global-ai-title"
    tabindex="-1"
    @keydown.esc.prevent="requestClose"
  >
    <div
      class="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-n-solid-2 outline outline-1 outline-n-container"
      @click.stop
      @mousedown.stop
    >
      <div
        class="flex flex-shrink-0 items-start justify-between gap-3 border-b border-n-container px-6 py-4"
      >
        <div class="min-w-0">
          <p class="text-xs font-medium text-n-slate-11">
            IA do Kanban (regra da conta) · Etapa {{ currentStep }} de
            {{ TOTAL_STEPS }}
          </p>
          <h2
            id="kanban-global-ai-title"
            class="mt-0.5 text-heading-2 text-n-slate-12"
          >
            {{ currentStepMeta.title }}
          </h2>
          <p class="mt-0.5 text-xs text-n-slate-11">
            {{ currentStepMeta.subtitle }}
          </p>
        </div>
        <button
          type="button"
          aria-label="Fechar configuração de IA do Kanban"
          class="flex-shrink-0 rounded-md p-1.5 text-n-slate-11 hover:bg-n-alpha-2"
          @click="requestClose"
        >
          <span class="i-ri-close-line block size-4" />
        </button>
      </div>

      <!-- Indicador de etapas -->
      <div class="flex-shrink-0 border-b border-n-container px-6 py-4">
        <div class="flex items-center justify-center gap-0">
          <template v-for="(step, index) in STEPS" :key="step.key">
            <div class="flex flex-col items-center gap-1">
              <div
                class="flex size-8 items-center justify-center rounded-full text-sm font-semibold transition-colors"
                :class="{
                  'bg-n-blue-9 text-white': currentStep === index + 1,
                  'bg-n-teal-9 text-white': currentStep > index + 1,
                  'bg-n-alpha-2 text-n-slate-11': currentStep < index + 1,
                }"
              >
                <span
                  v-if="currentStep > index + 1"
                  class="i-ri-check-line block size-4"
                />
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span
                class="whitespace-nowrap text-xs font-medium"
                :class="{
                  'text-n-blue-11': currentStep === index + 1,
                  'text-n-teal-11': currentStep > index + 1,
                  'text-n-slate-9': currentStep < index + 1,
                }"
              >
                {{ step.label }}
              </span>
            </div>
            <div
              v-if="index < STEPS.length - 1"
              class="mt-[-12px] h-px w-10 transition-colors"
              :class="currentStep > index + 1 ? 'bg-n-teal-9' : 'bg-n-alpha-3'"
            />
          </template>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto px-6 py-5">
        <div
          v-if="loading"
          class="flex items-center gap-2 text-sm text-n-slate-11"
        >
          <span class="i-ri-loader-2-line block size-4 animate-spin" />
          Carregando...
        </div>

        <template v-else>
          <!-- Etapa 1: funis que participam -->
          <div v-if="currentStepKey === 'funnels'" class="space-y-5">
            <div class="flex items-start justify-between gap-4">
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Criar cards automaticamente com IA
                </p>
                <p class="mt-0.5 text-xs text-n-slate-11">
                  Quando ligado, a configuração de IA dentro de cada funil deixa
                  de valer para a criação de card.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-label="Criar cards automaticamente com IA"
                :aria-checked="form.enabled ? 'true' : 'false'"
                class="group relative h-4 w-7 flex-shrink-0 select-none rounded-full transition-colors duration-200"
                :class="form.enabled ? 'bg-n-brand' : 'bg-n-slate-6'"
                @click="form.enabled = !form.enabled"
              >
                <span
                  class="absolute left-0.5 top-1/2 -translate-y-1/2 transition-transform duration-200"
                  :class="form.enabled ? 'translate-x-3' : 'translate-x-0'"
                >
                  <span
                    class="block size-3 rounded-full bg-n-background shadow-md"
                  />
                </span>
              </button>
            </div>

            <p
              v-if="!form.enabled"
              class="rounded-xl border border-n-weak bg-n-alpha-1 p-3 text-xs text-n-slate-11"
            >
              A regra está desligada. Você pode seguir pelas próximas etapas
              para deixar tudo pronto — a configuração fica salva e passa a
              valer quando você ligar.
            </p>

            <template v-if="form.enabled">
              <div>
                <label class="mb-2 block text-label-small text-n-slate-11">
                  Funis que participam da regra
                  <span class="text-red-500">*</span>
                </label>
                <p v-if="!activeFunnels.length" class="text-xs text-n-slate-9">
                  Nenhum funil ativo nesta conta.
                </p>
                <div v-else class="space-y-1.5">
                  <label
                    v-for="funnel in activeFunnels"
                    :key="funnel.id"
                    class="flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 transition-colors"
                    :class="
                      isSelected(funnel.id)
                        ? 'border-n-blue-9 bg-n-blue-3'
                        : 'border-n-weak hover:border-n-slate-7 hover:bg-n-alpha-1'
                    "
                  >
                    <input
                      type="checkbox"
                      class="accent-n-blue-9"
                      :aria-label="funnel.name"
                      :checked="isSelected(funnel.id)"
                      @change="toggleFunnel(funnel.id)"
                    />
                    <span class="min-w-0 flex-1">
                      <span class="block truncate text-sm text-n-slate-12">
                        {{ funnel.name }}
                      </span>
                      <span class="block text-xs text-n-slate-10">
                        {{ Object.keys(funnel.stages || {}).length }} etapas
                      </span>
                    </span>
                  </label>
                </div>
                <p v-if="!selectedCount" class="mt-1.5 text-xs text-n-ruby-11">
                  Escolha pelo menos um funil para ligar a regra.
                </p>
              </div>

              <div>
                <label class="mb-1.5 block text-label-small text-n-slate-11">
                  Funil de fallback
                </label>
                <select
                  v-model="form.fallback_funnel_id"
                  aria-label="Funil de fallback"
                  class="w-full rounded-xl border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-9 focus:outline-none"
                >
                  <option :value="null">Usar o primeiro funil marcado</option>
                  <option
                    v-for="funnel in activeFunnels"
                    :key="funnel.id"
                    :value="funnel.id"
                  >
                    {{ funnel.name }}
                  </option>
                </select>
                <p class="mt-1 text-xs text-n-slate-9">
                  Recebe o card quando a IA não conseguir decidir o funil com
                  segurança — evita que o lead entre no funil errado.
                </p>
              </div>

              <div
                v-if="legacyFunnels.length"
                class="rounded-xl border border-n-amber-6 bg-n-amber-2 p-3 text-xs text-n-amber-11"
              >
                <p class="font-medium">
                  {{ legacyFunnels.length }}
                  {{
                    legacyFunnels.length === 1
                      ? 'funil ainda tem'
                      : 'funis ainda têm'
                  }}
                  a configuração antiga de IA ligada
                </p>
                <p class="mt-1">
                  {{ legacyFunnels.map(f => f.name).join(', ') }} — a config
                  dentro do funil continua valendo para "Atualizar com IA", mas
                  a criação de card passa a seguir esta regra global.
                </p>
              </div>
            </template>
          </div>

          <!-- Etapa 2: prompt de roteamento -->
          <div v-else-if="currentStepKey === 'routing'" class="space-y-3">
            <label class="block text-label-small text-n-slate-11">
              Como escolher o funil e a etapa
            </label>
            <textarea
              v-model="form.routing_prompt"
              aria-label="Regras de roteamento"
              rows="12"
              placeholder="Ex: quem quer comprar imóvel para morar vai em Compra e Moradia; quem quer alugar vai em Locação; quem oferece um imóvel vai em Captação."
              class="min-h-[260px] w-full resize-y rounded-xl border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-9 focus:outline-none"
            />
            <p class="text-xs text-n-slate-9">
              Escreva na linguagem da sua operação, citando os nomes dos funis e
              das etapas. Quando a conversa não deixar claro o destino, a IA
              manda o card para o funil de fallback em vez de chutar.
            </p>
            <p
              v-if="form.enabled && selectedFunnelNames.length"
              class="text-xs text-n-slate-11"
            >
              Funis disponíveis para a IA: {{ selectedFunnelNames.join(', ') }}
            </p>
          </div>

          <!-- Etapa 3: campos e instruções de extração -->
          <div v-else-if="currentStepKey === 'fields'" class="space-y-5">
            <div>
              <label class="mb-2 block text-label-small text-n-slate-11">
                Campos preenchidos automaticamente
              </label>
              <div class="space-y-1.5">
                <label
                  v-for="field in EXTRACT_FIELDS"
                  :key="field.key"
                  class="flex cursor-pointer items-start gap-3 rounded-xl border border-n-weak px-3 py-2 hover:bg-n-alpha-1"
                >
                  <input
                    v-model="form.extract_fields[field.key]"
                    type="checkbox"
                    class="mt-0.5 accent-n-blue-9"
                    :aria-label="field.label"
                  />
                  <span>
                    <span class="block text-sm text-n-slate-12">{{
                      field.label
                    }}</span>
                    <span class="block text-xs text-n-slate-10">{{
                      field.hint
                    }}</span>
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label class="mb-1.5 block text-label-small text-n-slate-11">
                Instruções de extração
              </label>
              <textarea
                v-model="form.custom_prompt"
                aria-label="Instruções de extração"
                rows="6"
                placeholder="Ex: atualize o card somente com fatos confirmados na conversa; não invente e não exponha CPF, telefone ou endereço exato."
                class="min-h-[140px] w-full resize-y rounded-xl border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-9 focus:outline-none"
              />
              <p class="mt-1 text-xs text-n-slate-9">
                Valem para os campos marcados acima.
              </p>
            </div>
          </div>

          <!-- Etapa 4: opções avançadas + resumo -->
          <div v-else class="space-y-5">
            <div
              class="flex items-center justify-between border-b border-n-container pb-3"
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  Anexar ofertas da conta
                </p>
                <p class="mt-0.5 text-xs text-n-slate-11">
                  A IA anexa ao card só as ofertas que aparecerem na conversa.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-label="Anexar ofertas da conta"
                :aria-checked="form.attach_offers ? 'true' : 'false'"
                class="group relative h-4 w-7 flex-shrink-0 select-none rounded-full transition-colors duration-200"
                :class="form.attach_offers ? 'bg-n-brand' : 'bg-n-slate-6'"
                @click="form.attach_offers = !form.attach_offers"
              >
                <span
                  class="absolute left-0.5 top-1/2 -translate-y-1/2 transition-transform duration-200"
                  :class="
                    form.attach_offers ? 'translate-x-3' : 'translate-x-0'
                  "
                >
                  <span
                    class="block size-3 rounded-full bg-n-background shadow-md"
                  />
                </span>
              </button>
            </div>

            <div class="border-b border-n-container pb-3">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    Atualizar o card após inatividade
                  </p>
                  <p class="mt-0.5 text-xs text-n-slate-11">
                    A IA revisa os campos do card quando a conversa fica um
                    tempo sem mensagem nova.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-label="Atualizar o card após inatividade"
                  :aria-checked="form.auto_update_enabled ? 'true' : 'false'"
                  class="group relative h-4 w-7 flex-shrink-0 select-none rounded-full transition-colors duration-200"
                  :class="
                    form.auto_update_enabled ? 'bg-n-brand' : 'bg-n-slate-6'
                  "
                  @click="form.auto_update_enabled = !form.auto_update_enabled"
                >
                  <span
                    class="absolute left-0.5 top-1/2 -translate-y-1/2 transition-transform duration-200"
                    :class="
                      form.auto_update_enabled
                        ? 'translate-x-3'
                        : 'translate-x-0'
                    "
                  >
                    <span
                      class="block size-3 rounded-full bg-n-background shadow-md"
                    />
                  </span>
                </button>
              </div>
              <div v-if="form.auto_update_enabled" class="mt-3">
                <label class="mb-1.5 block text-label-small text-n-slate-11">
                  Horas sem mensagem nova
                </label>
                <input
                  v-model.number="form.auto_update_after_hours"
                  type="number"
                  aria-label="Horas sem mensagem nova"
                  :min="MIN_HOURS"
                  :max="MAX_HOURS"
                  class="w-32 rounded-xl border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-9 focus:outline-none"
                />
                <p class="mt-1 text-xs text-n-slate-9">
                  Entre {{ MIN_HOURS }} e {{ MAX_HOURS }} horas.
                </p>
              </div>
            </div>

            <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
              <p class="text-sm font-medium text-n-slate-12">
                Resumo do que será salvo
              </p>
              <dl class="mt-3 space-y-2 text-xs">
                <div class="flex gap-2">
                  <dt class="w-40 flex-shrink-0 text-n-slate-11">Regra</dt>
                  <dd class="text-n-slate-12">
                    {{ form.enabled ? 'Ligada' : 'Desligada' }}
                  </dd>
                </div>
                <div class="flex gap-2">
                  <dt class="w-40 flex-shrink-0 text-n-slate-11">Funis</dt>
                  <dd class="text-n-slate-12">
                    {{
                      selectedFunnelNames.length
                        ? selectedFunnelNames.join(', ')
                        : 'Nenhum funil marcado'
                    }}
                  </dd>
                </div>
                <div class="flex gap-2">
                  <dt class="w-40 flex-shrink-0 text-n-slate-11">Fallback</dt>
                  <dd class="text-n-slate-12">{{ fallbackFunnelName }}</dd>
                </div>
                <div class="flex gap-2">
                  <dt class="w-40 flex-shrink-0 text-n-slate-11">Roteamento</dt>
                  <dd class="text-n-slate-12">
                    {{
                      form.routing_prompt
                        ? `${form.routing_prompt.length} caracteres`
                        : 'Sem instruções — a IA decide só pelos nomes das etapas'
                    }}
                  </dd>
                </div>
                <div class="flex gap-2">
                  <dt class="w-40 flex-shrink-0 text-n-slate-11">Campos</dt>
                  <dd class="text-n-slate-12">
                    {{
                      selectedFieldLabels.length
                        ? selectedFieldLabels.join(', ')
                        : 'Nenhum campo marcado'
                    }}
                  </dd>
                </div>
                <div class="flex gap-2">
                  <dt class="w-40 flex-shrink-0 text-n-slate-11">Ofertas</dt>
                  <dd class="text-n-slate-12">
                    {{ form.attach_offers ? 'Anexar' : 'Não anexar' }}
                  </dd>
                </div>
                <div class="flex gap-2">
                  <dt class="w-40 flex-shrink-0 text-n-slate-11">
                    Autoatualização
                  </dt>
                  <dd class="text-n-slate-12">
                    {{
                      form.auto_update_enabled
                        ? `Após ${normalizeHours(form.auto_update_after_hours)}h sem mensagem`
                        : 'Desligada'
                    }}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </template>
      </div>

      <div
        v-if="errorMessage"
        class="mx-6 mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20"
      >
        <p class="text-sm text-red-700 dark:text-red-400">{{ errorMessage }}</p>
      </div>

      <div
        class="flex flex-shrink-0 items-center justify-between gap-2 border-t border-n-container px-6 py-4"
      >
        <button
          type="button"
          class="rounded-xl border border-n-strong px-3 py-1.5 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="currentStep === 1"
          @click.stop.prevent="prevStep"
        >
          Anterior
        </button>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="rounded-xl border border-n-strong px-3 py-1.5 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
            @click="requestClose"
          >
            Cancelar
          </button>
          <button
            v-if="currentStep < TOTAL_STEPS"
            type="button"
            class="rounded-xl bg-n-blue-9 px-4 py-1.5 text-sm font-medium text-white hover:bg-n-blue-10 disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="!canProceed"
            @click.stop.prevent="nextStep"
          >
            Próximo
          </button>
          <button
            v-else
            type="button"
            class="inline-flex items-center gap-1.5 rounded-xl bg-n-blue-9 px-4 py-1.5 text-sm font-medium text-white hover:bg-n-blue-10 disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="saving || loading || !canSave"
            @click.stop.prevent="save"
          >
            <span
              v-if="saving"
              class="i-ri-loader-2-line block size-4 animate-spin"
            />
            Salvar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
